import { Link } from 'react-router-dom';
import { Box, Typography } from '@mui/material';

const NAV_MODE_LABELS = {
    main: 'MAIN MODE',
    edit: 'EDIT MODE',
    view: 'VIEW MODE',
};

function PremiumPageNavTab({ label, to, active, disabled }) {
    if (disabled) {
        return (
            <span
                className={`premium-page-header-nav-tab${active ? ' is-active' : ''} is-disabled`}
                aria-current={active ? 'page' : undefined}
            >
                {label}
            </span>
        );
    }

    if (active) {
        return (
            <span className="premium-page-header-nav-tab is-active" aria-current="page">
                {label}
            </span>
        );
    }

    return (
        <Link to={to} className="premium-page-header-nav-tab">
            {label}
        </Link>
    );
}

function PremiumPageHeader({
    module,
    title,
    subtitle,
    modeLabel,
    headerActions,
    activeNav,
    mainLink,
    viewLink,
    editLink,
    headerBackground,
}) {
    const resolvedModeLabel = modeLabel || (activeNav ? NAV_MODE_LABELS[activeNav] : null);
    const showNavBar = Boolean(activeNav || mainLink || viewLink);

    return (
        <Box
            sx={{
                px: { xs: 2, md: 3 },
                py: { xs: 2, md: 2.5 },
                mb: 2.5,
                borderRadius: '16px',
                background: headerBackground || 'linear-gradient(135deg, #eef2ff 0%, #f8fafc 55%, #ffffff 100%)',
                border: '1px solid #dbe4f3',
                boxShadow: '0 8px 24px rgba(30, 58, 138, 0.08)',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            letterSpacing: '0.18em',
                            textTransform: 'uppercase',
                            color: '#4f6baf',
                            mb: 0.5,
                        }}
                    >
                        {module}
                    </Typography>
                    <Typography
                        component="h1"
                        sx={{
                            fontSize: { xs: '1.35rem', md: '1.75rem' },
                            fontWeight: 700,
                            color: '#0f1b3d',
                            lineHeight: 1.2,
                            mb: subtitle ? 0.75 : 0,
                        }}
                    >
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography
                            sx={{
                                fontSize: '0.9rem',
                                color: '#64748b',
                                lineHeight: 1.45,
                                maxWidth: 720,
                            }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>
                    {headerActions}
                    {resolvedModeLabel && (
                        <Box
                            sx={{
                                px: 2.5,
                                py: 1,
                                borderRadius: '999px',
                                background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 55%, #2563eb 100%)',
                                color: '#fff',
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                                boxShadow: '0 6px 16px rgba(37, 99, 235, 0.35)',
                            }}
                        >
                            {resolvedModeLabel}
                        </Box>
                    )}
                </Box>
            </Box>

            {showNavBar && (
                <Box
                    className="premium-page-header-nav-row"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap',
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid #dbe4f3',
                        width: '100%',
                    }}
                >
                    <nav className="premium-page-header-nav" aria-label="Page navigation">
                        {mainLink && (
                            <PremiumPageNavTab
                                label="Main"
                                to={mainLink}
                                active={activeNav === 'main'}
                            />
                        )}
                        <PremiumPageNavTab
                            label="Edit"
                            to={editLink}
                            active={activeNav === 'edit'}
                            disabled={!editLink && activeNav !== 'edit'}
                        />
                        {viewLink && (
                            <PremiumPageNavTab
                                label="Views"
                                to={viewLink}
                                active={activeNav === 'view'}
                            />
                        )}
                    </nav>
                </Box>
            )}
        </Box>
    );
}

export default PremiumPageHeader;
